import { useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { ApiError } from '@/api/client'
import { profileApi, referenceApi, resumeApi } from '@/api/endpoints'
import { Button } from '@/components/ui/Button'
import { TextArea, TextField } from '@/components/ui/Field'
import { Combobox } from '@/components/ui/Select'
import { StatusPill, type Tone } from '@/components/ui/StatusPill'
import { YesNo } from '@/components/ui/Toggle'
import { cn, formatBytes, formatDate, humanise } from '@/lib/utils'
import { useAuthStore } from '@/store/auth'
import { toast } from '@/store/toast'
import type { Profile, Resume, WorkAuthorizationPayload } from '@/types/api'

import { EnhanceToolbar } from './Enhance'
import { ResumeScanModal } from './ResumeScanModal'
import {
  AddButton,
  FORM_GRID,
  FORM_SPAN,
  FieldGroup,
  NOTE_QUESTIONS,
  RemoveButton,
  SaveFooter,
  SectionCard,
  YesNoPills,
  composeNotes,
  fileExtension,
  parseNotes,
  useSectionForm,
  type NoteValues,
} from './shared'

const ACCEPT = '.pdf,.doc,.docx,.txt'
const MAX_BYTES = 10 * 1024 * 1024
/** The pill tone for where a resume is in its scan. */
const RESUME_TONE: Record<string, Tone> = {
  parsed: 'success',
  failed: 'danger',
  pending: 'info',
  parsing: 'info',
}

interface Props {
  profile: Profile
  resumes: Resume[] | undefined
}

export function ProfileTab({ profile, resumes }: Props) {
  return (
    <div className="space-y-4">
      <ResumeSection resumes={resumes} />
      <PersonalInfoSection profile={profile} />
      <CareerOverviewSection profile={profile} />
      <WorkAuthorizationSection profile={profile} />
      <SecurityClearanceSection profile={profile} />
      <OnlineProfilesSection profile={profile} />
      <EducationSection profile={profile} />
      <QuestionsSection profile={profile} />
    </div>
  )
}

/** Shared mutation plumbing: refetch the profile and say "Saved". */
function useSave(mutationFn: () => Promise<unknown>, onSaved?: () => void) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profile'] })
      queryClient.invalidateQueries({ queryKey: ['profile-recommendations'] })
      queryClient.invalidateQueries({ queryKey: ['career-paths'] })
      toast.success('Saved')
      onSaved?.()
    },
    onError: (error: ApiError) => toast.error('Could not save', error.message),
  })
}

// ---------------------------------------------------------------------------
// Resume
// ---------------------------------------------------------------------------
function ResumeSection({ resumes }: { resumes: Resume[] | undefined }) {
  const queryClient = useQueryClient()
  const fileInput = useRef<HTMLInputElement>(null)
  const [dragging, setDragging] = useState(false)
  const [pending, setPending] = useState<File | null>(null)
  const [rejected, setRejected] = useState<string | null>(null)

  const accept = (file: File | undefined) => {
    if (!file) return
    setRejected(null)
    if (file.size > MAX_BYTES) {
      setRejected(`That file is ${formatBytes(file.size)}. The limit is 10 MB.`)
      return
    }
    setPending(file)
  }

  const setDefault = useMutation({
    mutationFn: (id: string) => resumeApi.setDefault(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['resumes'] })
      toast.success('Default resume updated')
    },
    onError: (error: ApiError) => toast.error('Could not update', error.message),
  })
  const remove = useMutation({
    mutationFn: (id: string) => resumeApi.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['resumes'] })
      queryClient.invalidateQueries({ queryKey: ['profile-recommendations'] })
      queryClient.invalidateQueries({ queryKey: ['career-paths'] })
      toast.info('Resume deleted')
    },
    onError: (error: ApiError) => toast.error('Could not delete', error.message),
  })

  return (
    <SectionCard
      id="profile-resume"
      title="Upload your resume"
      description="Upload your resume and we'll extract your skills automatically. The default one is attached to applications."
      actions={
        resumes && resumes.length > 0 ? (
          <span className="text-[12.5px] text-ink-muted">
            <span className="num font-medium text-ink">{resumes.length}</span>{' '}
            {resumes.length === 1 ? 'file' : 'files'}
          </span>
        ) : undefined
      }
    >
      <div
        onDragOver={(event) => {
          event.preventDefault()
          setDragging(true)
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(event) => {
          event.preventDefault()
          setDragging(false)
          accept(event.dataTransfer.files?.[0])
        }}
        onClick={() => fileInput.current?.click()}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            fileInput.current?.click()
          }
        }}
        role="button"
        tabIndex={0}
        className={cn(
          'flex cursor-pointer flex-col items-center gap-3 rounded-card border-2 border-dashed px-4 py-6 text-center transition-colors sm:flex-row sm:px-5 sm:py-5 sm:text-left',
          dragging
            ? 'border-brand bg-brand-soft'
            : 'border-line-strong/70 bg-surface-muted/60 hover:border-line-strong',
        )}
      >
        <input
          ref={fileInput}
          type="file"
          accept={ACCEPT}
          className="sr-only"
          onChange={(event) => {
            accept(event.target.files?.[0])
            event.target.value = ''
          }}
        />
        <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-brand-soft text-brand">
          <svg className="h-5 w-5" viewBox="0 0 40 40" fill="none" aria-hidden="true">
            <path
              d="M20 27V11m0 0-6 6m6-6 6 6"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M8 26v3a3 3 0 0 0 3 3h18a3 3 0 0 0 3-3v-3"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
            />
          </svg>
        </span>
        <span className="min-w-0">
          <span className="block text-[15px] text-ink">
            Drop your resume here, or{' '}
            <span className="font-medium underline underline-offset-4">choose a file</span>
          </span>
          <span className="mt-0.5 block text-[13px] text-ink-muted">
            PDF, Word or plain text, up to 10 MB.
          </span>
        </span>
      </div>
      {rejected && (
        <p
          role="alert"
          className="mt-3 rounded-field bg-danger/8 px-4 py-2.5 text-[13.5px] text-danger"
        >
          {rejected}
        </p>
      )}

      {resumes && resumes.length > 0 && (
        <ul className="panel mt-4 divide-y divide-line">
          {resumes.map((resume) => (
            <li key={resume.id} className="p-3.5 sm:px-4">
              <div className="flex items-start gap-3">
                <span
                  aria-hidden
                  className="grid h-10 w-10 shrink-0 place-items-center rounded-[9px] bg-surface-muted text-[10px] font-semibold uppercase tracking-wide text-ink-muted"
                >
                  {fileExtension(resume.fileName)}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[14.5px] font-medium text-ink">{resume.fileName}</p>
                  <p className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[12.5px] text-ink-muted">
                    <span className="num">{formatBytes(resume.sizeBytes)}</span>
                    <span aria-hidden className="text-ink-faint">
                      ·
                    </span>
                    <span className="num">{formatDate(resume.createdAt)}</span>
                  </p>
                </div>
                <span className="flex shrink-0 flex-col items-end gap-1 sm:flex-row sm:items-center">
                  {resume.isDefault && (
                    <StatusPill tone="brand" size="sm">
                      Default
                    </StatusPill>
                  )}
                  <StatusPill
                    size="sm"
                    tone={RESUME_TONE[resume.status] ?? 'info'}
                    live={resume.status !== 'parsed' && resume.status !== 'failed'}
                  >
                    {humanise(resume.status)}
                  </StatusPill>
                </span>
              </div>
              <div className="mt-2.5 flex flex-wrap items-center gap-1 pl-[52px]">
                {!resume.isDefault && (
                  <Button
                    size="xs"
                    variant="ghost"
                    onClick={() => setDefault.mutate(resume.id)}
                    loading={setDefault.isPending && setDefault.variables === resume.id}
                  >
                    Make default
                  </Button>
                )}
                <Button
                  size="xs"
                  variant="ghost"
                  onClick={() =>
                    resumeApi
                      .download(resume.id, resume.fileName)
                      .catch((error: ApiError) => toast.error('Download failed', error.message))
                  }
                >
                  Download
                </Button>
                <Button
                  size="xs"
                  variant="ghost"
                  className="ml-auto text-danger hover:text-danger"
                  onClick={() => remove.mutate(resume.id)}
                  loading={remove.isPending && remove.variables === resume.id}
                >
                  Delete
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <ResumeScanModal file={pending} onClose={() => setPending(null)} />
    </SectionCard>
  )
}

// ---------------------------------------------------------------------------
// Personal information
// ---------------------------------------------------------------------------
const personalFrom = (profile: Profile) => ({
  firstName: profile.firstName ?? '',
  lastName: profile.lastName ?? '',
  postalCode: profile.location?.postalCode ?? '',
  city: profile.location?.city ?? '',
  stateCode: profile.location?.stateCode ?? '',
  countryCode: profile.location?.countryCode ?? '',
  phoneNumber: profile.contact?.phoneNumber ?? '',
  dateOfBirth: profile.dateOfBirth ?? '',
  middleName: profile.middleName ?? '',
  maritalStatus: profile.maritalStatus ?? '',
  // Never the PAN itself: the box starts empty and shows the masked one on file as its placeholder.
  panNumber: '',
  hometownCity: profile.hometownCity ?? '',
  hometownState: profile.hometownState ?? '',
})

const MARITAL_OPTIONS = [
  { value: 'single', label: 'Single' },
  { value: 'married', label: 'Married' },
  { value: 'divorced', label: 'Divorced' },
  { value: 'widowed', label: 'Widowed' },
  { value: 'separated', label: 'Separated' },
  { value: 'prefer_not_to_say', label: 'Prefer not to say' },
]

function PersonalInfoSection({ profile }: { profile: Profile }) {
  const email = useAuthStore((state) => state.user?.email)
  const { form, update, dirty, markSaved, savedAt } = useSectionForm(profile, personalFrom)

  const { data: countries = [] } = useQuery({
    queryKey: ['countries'],
    queryFn: () => referenceApi.countries(),
    staleTime: Infinity,
  })
  const { data: states = [] } = useQuery({
    queryKey: ['states', form.countryCode],
    queryFn: () => referenceApi.states(form.countryCode),
    enabled: Boolean(form.countryCode),
    staleTime: Infinity,
  })
  const countryOptions = useMemo(
    () =>
      countries.map((country) => ({
        value: country.code,
        label: country.name,
        prefix: <span aria-hidden="true">{country.flag}</span>,
      })),
    [countries],
  )

  const save = useSave(async () => {
    const before = personalFrom(profile)
    if (
      form.firstName !== before.firstName ||
      form.lastName !== before.lastName ||
      form.dateOfBirth !== before.dateOfBirth ||
      form.middleName !== before.middleName ||
      form.maritalStatus !== before.maritalStatus ||
      form.panNumber !== before.panNumber ||
      form.hometownCity !== before.hometownCity ||
      form.hometownState !== before.hometownState
    ) {
      await profileApi.updateIdentity({
        firstName: form.firstName.trim() || null,
        lastName: form.lastName.trim() || null,
        dateOfBirth: form.dateOfBirth || null,
        middleName: form.middleName.trim() || null,
        maritalStatus: form.maritalStatus || null,
        // Sent only when typed; the one on file is kept otherwise.
        ...(form.panNumber.trim() ? { panNumber: form.panNumber.trim().toUpperCase() } : {}),
        hometownCity: form.hometownCity.trim() || null,
        hometownState: form.hometownState.trim() || null,
      })
    }
    if (form.phoneNumber !== before.phoneNumber) {
      // Contact is a full replace: the links are carried over as they are.
      await profileApi.updateContact({
        phoneNumber: form.phoneNumber.trim() || null,
        linkedinUrl: profile.contact?.linkedinUrl ?? null,
        githubUrl: profile.contact?.githubUrl ?? null,
        portfolioUrl: profile.contact?.portfolioUrl ?? null,
      })
    }
    const locationChanged =
      form.city !== before.city ||
      form.postalCode !== before.postalCode ||
      form.countryCode !== before.countryCode ||
      form.stateCode !== before.stateCode
    if (locationChanged) {
      if (!form.city.trim() || !form.countryCode) {
        throw new ApiError(
          'Add a city and a country to save your address',
          422,
          'validation_error',
          null,
        )
      }
      if (states.length > 0 && !form.stateCode) {
        throw new ApiError('Pick your state — employers ask for it with the address', 422, 'validation_error', null)
      }
      const previous = profile.location
      // A pin for another city or country would be wrong: it is dropped.
      const moved =
        form.city.trim() !== (previous?.city ?? '') ||
        form.countryCode !== (previous?.countryCode ?? '')
      const { locationLabel: _label, ...kept } = previous ?? ({} as Record<string, unknown>)
      const country = countries.find((c) => c.code === form.countryCode)
      const state = states.find((s) => s.code === form.stateCode)
      await profileApi.updateLocation({
        ...kept,
        city: form.city.trim(),
        postalCode: form.postalCode.trim() || null,
        countryCode: form.countryCode,
        countryName: country?.name ?? previous?.countryName ?? form.countryCode,
        stateCode: form.stateCode || null,
        stateName:
          state?.name ??
          (form.stateCode === previous?.stateCode ? (previous?.stateName ?? null) : null),
        ...(moved ? { latitude: null, longitude: null, countyDistrict: null } : {}),
      })
    }
  }, markSaved)

  return (
    <SectionCard
      id="profile-personal"
      title="Personal information"
      description="How employers reach you. Your name and phone go on every application."
      footer={
        <SaveFooter
          onSave={() => save.mutate()}
          saving={save.isPending}
          dirty={dirty}
          savedAt={savedAt}
        />
      }
    >
      <div className="space-y-3">
        <FieldGroup title="Identity">
          <TextField
            label="First name"
            required
            appearance="box"
            autoComplete="given-name"
            value={form.firstName}
            onChange={(event) => update('firstName', event.target.value)}
          />
          <TextField
            label="Last name"
            required
            appearance="box"
            autoComplete="family-name"
            value={form.lastName}
            onChange={(event) => update('lastName', event.target.value)}
          />
          <TextField
            label="Middle name"
            appearance="box"
            autoComplete="additional-name"
            value={form.middleName}
            onChange={(event) => update('middleName', event.target.value)}
            hint="Only if your ID has one; a form's middle-name box is otherwise left empty."
          />
        </FieldGroup>

        <FieldGroup title="Contact">
          <TextField
            label="Email"
            appearance="box"
            type="email"
            value={email ?? ''}
            readOnly
            className="bg-surface-muted/70 text-ink-muted"
            hint="Your sign-in address. Applications are sent from the address chosen in Settings."
          />
          <TextField
            label="Phone"
            appearance="box"
            type="tel"
            autoComplete="tel"
            value={form.phoneNumber}
            onChange={(event) => update('phoneNumber', event.target.value)}
            hint="With the country code, e.g. +91 9133453036."
          />
        </FieldGroup>

        <FieldGroup title="Address">
          <TextField
            label="Zip / Postal code"
            appearance="box"
            autoComplete="postal-code"
            value={form.postalCode}
            onChange={(event) => update('postalCode', event.target.value)}
          />
          <TextField
            label="City"
            appearance="box"
            autoComplete="address-level2"
            value={form.city}
            onChange={(event) => update('city', event.target.value)}
          />
          <Combobox
            label="State"
            appearance="box"
            options={states.map((s) => ({ value: s.code, label: s.name }))}
            value={form.stateCode || null}
            onChange={(code) => update('stateCode', code)}
            placeholder={form.countryCode ? 'Search state…' : 'Pick a country first'}
            disabled={!form.countryCode}
          />
          <Combobox
            label="Country"
            appearance="box"
            options={countryOptions}
            value={form.countryCode || null}
            onChange={(code) => {
              // A state from the previous country would be nonsense.
              if (code !== form.countryCode) update('stateCode', '')
              update('countryCode', code)
            }}
            placeholder="Search country…"
          />
          <TextField
            label="Hometown city"
            appearance="box"
            value={form.hometownCity}
            onChange={(event) => update('hometownCity', event.target.value)}
            hint="Where you are from, when a form asks it apart from your address."
          />
          <TextField
            label="Hometown state"
            appearance="box"
            value={form.hometownState}
            onChange={(event) => update('hometownState', event.target.value)}
          />
        </FieldGroup>

        <FieldGroup title="Personal details">
          <Combobox
            label="Marital status"
            appearance="box"
            options={MARITAL_OPTIONS}
            value={form.maritalStatus || null}
            onChange={(value) => update('maritalStatus', value)}
            placeholder="Asked on some Indian portals"
          />
          <TextField
            label="PAN"
            appearance="box"
            autoComplete="off"
            maxLength={10}
            placeholder={profile.panNumberMasked ?? 'ABCDE1234F'}
            value={form.panNumber}
            onChange={(event) => update('panNumber', event.target.value.toUpperCase())}
            hint={
              profile.panNumberMasked
                ? 'On file, stored encrypted. Type a new one to replace it.'
                : 'Stored encrypted; typed only where an employer form requires it.'
            }
          />
        </FieldGroup>

        <FieldGroup title="Date of birth">
          <TextField
            label="Date of birth"
            appearance="box"
            type="date"
            autoComplete="bday"
            value={form.dateOfBirth}
            onChange={(event) => update('dateOfBirth', event.target.value)}
            hint="Typed only where a job portal or employer form requires it."
          />
        </FieldGroup>
      </div>
    </SectionCard>
  )
}

// ---------------------------------------------------------------------------
// Career overview
// ---------------------------------------------------------------------------
const careerFrom = (profile: Profile) => ({
  headline: profile.headline ?? '',
  summary: profile.summary ?? '',
  currentTitle: profile.currentTitle ?? '',
  currentCompany: profile.currentCompany ?? '',
  totalExperienceYears: profile.totalExperienceYears?.toString() ?? '',
  noticePeriodDays: profile.noticePeriodDays?.toString() ?? '',
  currentSalary: profile.currentSalary?.toString() ?? '',
  expectedSalary: profile.expectedSalary?.toString() ?? '',
  variablePay: profile.variablePay?.toString() ?? '',
})

const numberOrNull = (text: string) => (text.trim() === '' ? null : Number(text))

function CareerOverviewSection({ profile }: { profile: Profile }) {
  const { form, update, dirty, markSaved, savedAt } = useSectionForm(profile, careerFrom)
  const currency = profile.salaryCurrency ?? profile.jobPreference?.salaryCurrency ?? 'INR'
  const [rewriting, setRewriting] = useState<{
    headline: boolean
    summary: boolean
  }>({
    headline: false,
    summary: false,
  })
  // What the rewrite is told about the person: the draft, so unsaved edits count too.
  const enhanceContext = {
    title: form.currentTitle.trim() || null,
    company: form.currentCompany.trim() || null,
    skills: (profile.skills ?? []).map((skill) => skill.name),
    totalExperienceYears: numberOrNull(form.totalExperienceYears),
  }

  const save = useSave(
    () =>
      profileApi.updateIdentity({
        headline: form.headline.trim() || null,
        summary: form.summary.trim() || null,
        currentTitle: form.currentTitle.trim() || null,
        currentCompany: form.currentCompany.trim() || null,
        totalExperienceYears: numberOrNull(form.totalExperienceYears),
        noticePeriodDays: numberOrNull(form.noticePeriodDays),
        currentSalary: numberOrNull(form.currentSalary),
        expectedSalary: numberOrNull(form.expectedSalary),
        variablePay: numberOrNull(form.variablePay),
      }),
    markSaved,
  )

  return (
    <SectionCard
      id="profile-career"
      title="Career overview"
      description="Your current role and what you are looking for next. Asked on almost every application."
      footer={
        <SaveFooter
          onSave={() => save.mutate()}
          saving={save.isPending}
          dirty={dirty}
          savedAt={savedAt}
        />
      }
    >
      <div className="space-y-3">
        <FieldGroup title="Headline and summary">
          <div className={FORM_SPAN}>
            <TextField
              id="profile-edit-headline"
              label="Headline"
              appearance="box"
              placeholder="Senior Backend Engineer · Go, Kubernetes, AWS"
              value={form.headline}
              disabled={rewriting.headline}
              onChange={(event) => update('headline', event.target.value)}
            />
            <EnhanceToolbar
              kind="headline"
              text={form.headline}
              context={enhanceContext}
              onResult={(text) => update('headline', text)}
              onBusyChange={(busy) => setRewriting((current) => ({ ...current, headline: busy }))}
            />
          </div>
          <div className={FORM_SPAN}>
            <TextArea
              id="profile-edit-summary"
              label="Summary / About"
              appearance="box"
              rows={5}
              maxLength={5000}
              placeholder="A few sentences on what you do, the systems you have built, and what you are looking for next."
              value={form.summary}
              disabled={rewriting.summary}
              onChange={(event) => update('summary', event.target.value)}
              hint="Shown at the top of your profile preview and quoted in applications."
            />
            <EnhanceToolbar
              kind="summary"
              text={form.summary}
              context={enhanceContext}
              onResult={(text) => update('summary', text)}
              onBusyChange={(busy) => setRewriting((current) => ({ ...current, summary: busy }))}
            />
          </div>
        </FieldGroup>

        <FieldGroup title="Current role">
          <TextField
            id="profile-edit-currentTitle"
            label="Current title"
            appearance="box"
            value={form.currentTitle}
            onChange={(event) => update('currentTitle', event.target.value)}
          />
          <TextField
            id="profile-edit-currentCompany"
            label="Current company"
            appearance="box"
            value={form.currentCompany}
            onChange={(event) => update('currentCompany', event.target.value)}
          />
          <TextField
            id="profile-edit-totalExperienceYears"
            label="Years of experience"
            appearance="box"
            type="number"
            inputMode="decimal"
            min={0}
            max={60}
            step={0.5}
            value={form.totalExperienceYears}
            onChange={(event) => update('totalExperienceYears', event.target.value)}
          />
          <TextField
            id="profile-edit-noticePeriodDays"
            label="Notice period (days)"
            appearance="box"
            type="number"
            min={0}
            value={form.noticePeriodDays}
            onChange={(event) => update('noticePeriodDays', event.target.value)}
            hint="0 if you can join immediately."
          />
        </FieldGroup>

        <FieldGroup title="Compensation">
          <TextField
            id="profile-edit-currentSalary"
            label="Current salary (CTC)"
            appearance="box"
            type="number"
            min={0}
            value={form.currentSalary}
            onChange={(event) => update('currentSalary', event.target.value)}
            hint={`Yearly, in ${currency}.`}
          />
          <TextField
            id="profile-edit-expectedSalary"
            label="Expected salary"
            appearance="box"
            type="number"
            min={0}
            value={form.expectedSalary}
            onChange={(event) => update('expectedSalary', event.target.value)}
            hint={`Yearly, in ${currency}.`}
          />
          <TextField
            id="profile-edit-variablePay"
            label="Variable pay"
            appearance="box"
            type="number"
            min={0}
            value={form.variablePay}
            onChange={(event) => update('variablePay', event.target.value)}
            hint={`Yearly bonus or variable component, in ${currency}; 0 if none. Asked beside the fixed pay on some forms.`}
          />
        </FieldGroup>
      </div>
    </SectionCard>
  )
}

// ---------------------------------------------------------------------------
// Work authorization
// ---------------------------------------------------------------------------
interface AuthRow extends WorkAuthorizationPayload {
  /** Stable key for a row while it is being edited. */
  key: string
}

const authorizationsFrom = (profile: Profile): AuthRow[] =>
  (profile.workEligibility?.workAuthorizations ?? []).map((row) => ({
    key: row.id ?? row.countryCode,
    countryCode: row.countryCode,
    countryName: row.countryName,
    legallyAuthorized: row.legallyAuthorized ?? true,
    requiresSponsorship: row.requiresSponsorship ?? false,
    authorizationBasis: row.authorizationBasis ?? null,
    visaExpiresOn: row.visaExpiresOn ?? null,
    notes: row.notes ?? null,
  }))

function WorkAuthorizationSection({ profile }: { profile: Profile }) {
  const {
    form: rows,
    setForm: setRows,
    dirty,
    markSaved,
    savedAt,
  } = useSectionForm(profile, authorizationsFrom)
  const { data: countries = [] } = useQuery({
    queryKey: ['countries'],
    queryFn: () => referenceApi.countries(),
    staleTime: Infinity,
  })
  const countryOptions = useMemo(
    () =>
      countries.map((country) => ({
        value: country.code,
        label: country.name,
        prefix: <span aria-hidden="true">{country.flag}</span>,
      })),
    [countries],
  )

  const patch = (key: string, changes: Partial<AuthRow>) =>
    setRows((current) => current.map((row) => (row.key === key ? { ...row, ...changes } : row)))

  const save = useSave(async () => {
    const complete = rows.filter((row) => row.countryCode)
    const codes = complete.map((row) => row.countryCode)
    if (new Set(codes).size !== codes.length) {
      throw new ApiError('Each country can only be listed once', 422, 'validation_error', null)
    }
    await profileApi.updateWorkEligibility({
      citizenshipCountryCodes: profile.workEligibility?.citizenshipCountryCodes ?? [],
      workAuthorizations: complete.map(({ key: _key, ...row }) => row),
    })
  }, markSaved)

  return (
    <SectionCard
      id="profile-work-authorization"
      eyebrow="Work eligibility"
      title="Work authorization"
      description="Where you can legally work, and whether you would need sponsorship. Answers the visa questions for you."
      actions={
        rows.length > 0 ? (
          <span className="text-[12.5px] text-ink-muted">
            <span className="num font-medium text-ink">{rows.length}</span>{' '}
            {rows.length === 1 ? 'country' : 'countries'}
          </span>
        ) : undefined
      }
      footer={
        <SaveFooter
          onSave={() => save.mutate()}
          saving={save.isPending}
          dirty={dirty}
          savedAt={savedAt}
        />
      }
    >
      {rows.length === 0 ? (
        <p className="panel px-4 py-3 text-[14px] text-ink-muted">No countries added yet.</p>
      ) : (
        <ul className="space-y-3">
          {rows.map((row) => (
            <li key={row.key} className="panel p-4 sm:p-5">
              <div className="flex items-start gap-3">
                <div className="min-w-0 flex-1">
                  <Combobox
                    label="Country"
                    appearance="box"
                    options={countryOptions}
                    value={row.countryCode || null}
                    onChange={(code) =>
                      patch(row.key, {
                        countryCode: code,
                        countryName: countries.find((c) => c.code === code)?.name ?? code,
                      })
                    }
                    placeholder="Search country…"
                  />
                </div>
                <div className="pt-7">
                  <RemoveButton
                    onClick={() => setRows((current) => current.filter((r) => r.key !== row.key))}
                    label={`Remove ${row.countryName || 'country'}`}
                  />
                </div>
              </div>
              <div className={cn('mt-4', FORM_GRID)}>
                <div className="flex min-h-[44px] items-center justify-between gap-3 rounded-field bg-surface-muted/60 px-3.5 py-2">
                  <span className="text-[14px] text-ink-soft">Legally authorized</span>
                  <YesNo
                    value={row.legallyAuthorized ?? true}
                    onChange={(value) => patch(row.key, { legallyAuthorized: value })}
                    aria-label={`Legally authorized to work in ${row.countryName}`}
                  />
                </div>
                <div className="flex min-h-[44px] items-center justify-between gap-3 rounded-field bg-surface-muted/60 px-3.5 py-2">
                  <span className="text-[14px] text-ink-soft">Needs sponsorship</span>
                  <YesNo
                    value={row.requiresSponsorship ?? false}
                    onChange={(value) => patch(row.key, { requiresSponsorship: value })}
                    aria-label={`Requires sponsorship in ${row.countryName}`}
                  />
                </div>
              </div>
              <div className="mt-3 flex flex-wrap gap-1.5">
                <StatusPill size="sm" tone={row.legallyAuthorized ? 'success' : 'neutral'}>
                  {row.legallyAuthorized ? 'Authorized' : 'Not authorized'}
                </StatusPill>
                {row.requiresSponsorship && (
                  <StatusPill size="sm" tone="warning">
                    Needs sponsorship
                  </StatusPill>
                )}
                {row.authorizationBasis && (
                  <StatusPill size="sm">{humanise(row.authorizationBasis)}</StatusPill>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
      <div className="mt-3">
        <AddButton
          onClick={() =>
            setRows((current) => [
              ...current,
              {
                key: `new-${Date.now()}`,
                countryCode: '',
                countryName: '',
                legallyAuthorized: true,
                requiresSponsorship: false,
                authorizationBasis: null,
                visaExpiresOn: null,
                notes: null,
              },
            ])
          }
        >
          Add country
        </AddButton>
      </div>
    </SectionCard>
  )
}

// ---------------------------------------------------------------------------
// Security clearance
// ---------------------------------------------------------------------------
const clearanceFrom = (profile: Profile) => ({
  hasGovernmentClearance: profile.checklist?.hasGovernmentClearance ?? false,
  clearanceDetails: profile.checklist?.clearanceDetails ?? '',
})

function SecurityClearanceSection({ profile }: { profile: Profile }) {
  const { form, update, dirty, markSaved, savedAt } = useSectionForm(profile, clearanceFrom)
  const answered = profile.checklist !== null

  const save = useSave(
    () =>
      profileApi.updateChecklist({
        ...profile.checklist!,
        hasGovernmentClearance: form.hasGovernmentClearance,
        clearanceDetails: form.hasGovernmentClearance ? form.clearanceDetails.trim() || null : null,
      }),
    markSaved,
  )

  return (
    <SectionCard
      id="profile-clearance"
      title="Security clearance"
      description="Some government and defence employers ask. Most people answer No."
      footer={
        answered ? (
          <SaveFooter
            onSave={() => save.mutate()}
            saving={save.isPending}
            dirty={dirty}
            savedAt={savedAt}
          />
        ) : undefined
      }
    >
      <div className="panel flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between sm:p-5">
        <p className="text-[15px] text-ink">Do you have a security clearance?</p>
        <YesNoPills
          value={answered ? form.hasGovernmentClearance : null}
          onChange={(value) => update('hasGovernmentClearance', value)}
          disabled={!answered}
          aria-label="Security clearance"
        />
      </div>
      {!answered && (
        <p className="mt-3 text-[13px] text-ink-faint">
          Not answered in setup yet. The checklist questions are answered once during onboarding.
        </p>
      )}
      {answered && form.hasGovernmentClearance && (
        <div className="mt-4">
          <TextField
            label="Clearance details"
            appearance="box"
            placeholder="Level, issuing country, and when it was granted"
            value={form.clearanceDetails}
            onChange={(event) => update('clearanceDetails', event.target.value)}
          />
        </div>
      )}
    </SectionCard>
  )
}

// ---------------------------------------------------------------------------
// Online profiles
// ---------------------------------------------------------------------------
const linksFrom = (profile: Profile) => ({
  linkedinUrl: profile.contact?.linkedinUrl ?? '',
  githubUrl: profile.contact?.githubUrl ?? '',
  portfolioUrl: profile.contact?.portfolioUrl ?? '',
})

function OnlineProfilesSection({ profile }: { profile: Profile }) {
  const { form, update, dirty, markSaved, savedAt } = useSectionForm(profile, linksFrom)
  const save = useSave(
    () =>
      profileApi.updateContact({
        phoneNumber: profile.contact?.phoneNumber ?? null,
        linkedinUrl: form.linkedinUrl.trim() || null,
        githubUrl: form.githubUrl.trim() || null,
        portfolioUrl: form.portfolioUrl.trim() || null,
      }),
    markSaved,
  )

  return (
    <SectionCard
      id="profile-links"
      title="Online profiles"
      description="Links employers can follow. LinkedIn is asked for on most applications."
      footer={
        <SaveFooter
          onSave={() => save.mutate()}
          saving={save.isPending}
          dirty={dirty}
          savedAt={savedAt}
        />
      }
    >
      <FieldGroup title="Links">
        <TextField
          id="profile-edit-linkedinUrl"
          label="LinkedIn URL"
          appearance="box"
          type="url"
          placeholder="https://linkedin.com/in/yourhandle"
          value={form.linkedinUrl}
          onChange={(event) => update('linkedinUrl', event.target.value)}
          trailing={<LinkIcon kind="linkedin" />}
        />
        <TextField
          label="GitHub URL"
          appearance="box"
          type="url"
          placeholder="https://github.com/yourhandle"
          value={form.githubUrl}
          onChange={(event) => update('githubUrl', event.target.value)}
          trailing={<LinkIcon kind="github" />}
        />
        <TextField
          label="Website / portfolio URL"
          appearance="box"
          type="url"
          placeholder="https://yourname.dev"
          value={form.portfolioUrl}
          onChange={(event) => update('portfolioUrl', event.target.value)}
          trailing={<LinkIcon kind="web" />}
          className={FORM_SPAN}
        />
      </FieldGroup>
    </SectionCard>
  )
}

function LinkIcon({ kind }: { kind: 'linkedin' | 'github' | 'web' }) {
  const path =
    kind === 'linkedin'
      ? 'M4.5 6.5v6M4.5 3.9v.2M8 12.5V9.3c0-1.3.8-2.1 2-2.1s2 .8 2 2.1v3.2M8 6.5v6'
      : kind === 'github'
        ? 'M10.5 13.5v-2.3c0-.6-.2-1-.5-1.3 1.8-.2 3-.9 3-3.5 0-.7-.2-1.3-.7-1.8.1-.3.2-1-.1-1.9 0 0-.7-.2-2.2.8a7.5 7.5 0 0 0-3.8 0C4.7 2.5 4 2.7 4 2.7c-.3.9-.2 1.6-.1 1.9-.5.5-.7 1.1-.7 1.8 0 2.6 1.2 3.3 3 3.5-.3.3-.5.7-.5 1.3v2.3'
        : 'M8 14.5a6.5 6.5 0 1 0 0-13 6.5 6.5 0 0 0 0 13ZM1.5 8h13M8 1.5c1.8 2 2.6 4.1 2.6 6.5S9.8 12.5 8 14.5C6.2 12.5 5.4 10.4 5.4 8S6.2 3.5 8 1.5Z'
  return (
    <span className="mr-3.5 text-ink-faint">
      <svg className="h-4 w-4" viewBox="0 0 16 16" fill="none" aria-hidden="true">
        <path
          d={path}
          stroke="currentColor"
          strokeWidth="1.3"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </span>
  )
}

// ---------------------------------------------------------------------------
// Education
// ---------------------------------------------------------------------------
const educationFrom = (profile: Profile) => ({
  educationCourse: profile.educationCourse ?? '',
  educationUniversity: profile.educationUniversity ?? '',
  educationGraduationYear: profile.educationGraduationYear?.toString() ?? '',
  tenthPercentage: profile.tenthPercentage?.toString() ?? '',
  twelfthPercentage: profile.twelfthPercentage?.toString() ?? '',
  degreeScore: profile.degreeScore?.toString() ?? '',
  degreeScoreScale: (profile.degreeScoreScale ?? 'percent') as string,
  educationRollNumber: profile.educationRollNumber ?? '',
})

const SCALE_OPTIONS = [
  { value: 'percent', label: 'Percentage' },
  { value: 'cgpa10', label: 'CGPA out of 10' },
  { value: 'gpa4', label: 'GPA out of 4' },
]

function EducationSection({ profile }: { profile: Profile }) {
  const { form, update, dirty, markSaved, savedAt } = useSectionForm(profile, educationFrom)
  const save = useSave(
    () =>
      profileApi.updateIdentity({
        educationCourse: form.educationCourse.trim() || null,
        educationUniversity: form.educationUniversity.trim() || null,
        educationGraduationYear: numberOrNull(form.educationGraduationYear),
        tenthPercentage: numberOrNull(form.tenthPercentage),
        twelfthPercentage: numberOrNull(form.twelfthPercentage),
        degreeScore: numberOrNull(form.degreeScore),
        degreeScoreScale: form.degreeScoreScale || null,
        educationRollNumber: form.educationRollNumber.trim() || null,
      }),
    markSaved,
  )

  return (
    <SectionCard
      id="profile-education"
      title="Education"
      description="Your highest qualification, as employer forms ask for it."
      footer={
        <SaveFooter
          onSave={() => save.mutate()}
          saving={save.isPending}
          dirty={dirty}
          savedAt={savedAt}
        />
      }
    >
      <FieldGroup title="Highest qualification">
        <TextField
          id="profile-edit-educationCourse"
          label="Degree / course"
          appearance="box"
          placeholder="B.Tech, Information Technology"
          value={form.educationCourse}
          onChange={(event) => update('educationCourse', event.target.value)}
          hint="Degree, then what you studied."
        />
        <TextField
          id="profile-edit-educationGraduationYear"
          label="Year passed out"
          appearance="box"
          type="number"
          min={1950}
          max={2100}
          placeholder="2011"
          value={form.educationGraduationYear}
          onChange={(event) => update('educationGraduationYear', event.target.value)}
        />
        <TextField
          id="profile-edit-educationUniversity"
          label="School or institution"
          appearance="box"
          placeholder="Jawaharlal Nehru Technological University, Anantapur"
          value={form.educationUniversity}
          onChange={(event) => update('educationUniversity', event.target.value)}
          hint="The full name, with the campus."
          className={FORM_SPAN}
        />
      </FieldGroup>

      <FieldGroup title="Marks">
        <TextField
          id="profile-edit-degreeScore"
          label="Degree score"
          appearance="box"
          type="number"
          min={0}
          max={100}
          step="0.01"
          placeholder="72.5"
          value={form.degreeScore}
          onChange={(event) => update('degreeScore', event.target.value)}
          hint="Your final aggregate, in the scale beside it. Typed only in that scale; never converted."
        />
        <Combobox
          label="Scale"
          appearance="box"
          options={SCALE_OPTIONS}
          value={form.degreeScoreScale || null}
          onChange={(value) => update('degreeScoreScale', value)}
        />
        <TextField
          id="profile-edit-tenthPercentage"
          label="10th percentage"
          appearance="box"
          type="number"
          min={0}
          max={100}
          step="0.01"
          placeholder="85"
          value={form.tenthPercentage}
          onChange={(event) => update('tenthPercentage', event.target.value)}
        />
        <TextField
          id="profile-edit-twelfthPercentage"
          label="12th percentage"
          appearance="box"
          type="number"
          min={0}
          max={100}
          step="0.01"
          placeholder="88"
          value={form.twelfthPercentage}
          onChange={(event) => update('twelfthPercentage', event.target.value)}
        />
        <TextField
          id="profile-edit-educationRollNumber"
          label="Roll / registration number"
          appearance="box"
          value={form.educationRollNumber}
          onChange={(event) => update('educationRollNumber', event.target.value)}
          hint="Of your highest qualification; some campus-style forms ask for it."
        />
      </FieldGroup>
    </SectionCard>
  )
}

// ---------------------------------------------------------------------------
// Questions employers keep asking (the checklist's notes)
// ---------------------------------------------------------------------------
const notesFrom = (profile: Profile): { answers: NoteValues; rest: string } => {
  const parsed = parseNotes(profile.checklist?.additionalNotes)
  return { answers: parsed.values, rest: parsed.rest }
}

function QuestionsSection({ profile }: { profile: Profile }) {
  const { form, setForm, dirty, markSaved, savedAt } = useSectionForm(profile, notesFrom)
  const save = useSave(
    () =>
      // The checklist is saved whole: everything else goes back as it was.
      profileApi.updateChecklist({
        ...(profile.checklist ?? {}),
        additionalNotes: composeNotes(form.answers, form.rest),
      } as Parameters<typeof profileApi.updateChecklist>[0]),
    markSaved,
  )

  return (
    <SectionCard
      id="profile-questions"
      title="Questions employers keep asking"
      description="Answer once; application forms that ask are filled from here. Leave blank what does not apply."
      footer={
        <SaveFooter
          onSave={() => save.mutate()}
          saving={save.isPending}
          dirty={dirty}
          savedAt={savedAt}
        />
      }
    >
      <div className="space-y-3">
        <FieldGroup title="Your answers">
          {NOTE_QUESTIONS.map((question) => (
            <TextField
              key={question.key}
              id={`profile-edit-note-${question.key}`}
              label={question.ask}
              appearance="box"
              type={'type' in question ? question.type : 'text'}
              min={'type' in question && question.type === 'number' ? 0 : undefined}
              placeholder={'placeholder' in question ? question.placeholder : undefined}
              value={form.answers[question.key]}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  answers: {
                    ...current.answers,
                    [question.key]: event.target.value,
                  },
                }))
              }
              className={'wide' in question && question.wide ? FORM_SPAN : undefined}
            />
          ))}
        </FieldGroup>
        <FieldGroup title="Anything else" grid={false}>
          <TextArea
            id="profile-notes"
            label="Anything else"
            appearance="box"
            rows={5}
            maxLength={4000}
            value={form.rest}
            onChange={(event) => setForm((current) => ({ ...current, rest: event.target.value }))}
            placeholder="Other facts an application might ask for, one per line."
            hint="Write only what is true; it is quoted on applications."
          />
        </FieldGroup>
        {profile.checklist && (
          <details className="panel px-4 py-3">
            <summary className="cursor-pointer py-1 text-[13.5px] font-medium text-ink-soft">
              Checklist answers from setup
            </summary>
            <dl className="mt-3 grid gap-x-6 gap-y-3 text-[13.5px] md:grid-cols-2">
              <ChecklistRow
                label="Open to in-person"
                value={profile.checklist.openToInPerson ? 'Yes' : 'No'}
              />
              <ChecklistRow
                label="Willing to relocate"
                value={profile.checklist.willingToRelocate ? 'Yes' : 'No'}
              />
              <ChecklistRow
                label="Can start immediately"
                value={profile.checklist.canStartImmediately ? 'Yes' : 'No'}
              />
              <ChecklistRow
                label="Reliable transportation"
                value={profile.checklist.hasReliableTransportation ? 'Yes' : 'No'}
              />
              <ChecklistRow label="Gender" value={profile.checklist.gender} />
              <ChecklistRow label="Race / ethnicity" value={profile.checklist.raceEthnicity} />
              <ChecklistRow
                label="Veteran status"
                value={humanise(profile.checklist.veteranStatus)}
              />
              <ChecklistRow
                label="Disability status"
                value={humanise(profile.checklist.disabilityStatus)}
              />
            </dl>
          </details>
        )}
      </div>
    </SectionCard>
  )
}

function ChecklistRow({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-ink-muted">{label}</dt>
      <dd className="text-right text-ink">
        {value || <span className="text-ink-faint">Not set</span>}
      </dd>
    </div>
  )
}
